/**
 * @file blog-images.ts
 * @summary Pool d'images SEO pour les articles de blog
 *
 * Fournit un pool d'images partagé (15 visuels) et une fonction de sélection déterministe
 * par slug d'article : chaque article reçoit toujours les mêmes 5 images, mais l'ordre et
 * le sous-ensemble varient d'un article à l'autre. Utilisé pour :
 *   - JSON-LD Article.image[] (Google Rich Results : image dans les SERP)
 *   - balises og:image multiples (Bing, Facebook, LinkedIn)
 *   - sitemap-images.xml (Google Image Search)
 *   - image hero affichée en haut de l'article + 1-2 dans le corps
 *
 * @see https://developers.google.com/search/docs/appearance/structured-data/article
 * @see https://developers.google.com/search/docs/crawling-indexing/sitemaps/image-sitemaps
 */

const SITE = "https://crossfitchistera.fr";

export interface BlogImage {
  /** chemin relatif depuis /public (ex: /images/blog/foo.jpg) */
  path: string;
  alt: string;
  title: string;
  caption: string;
  width: number;
  height: number;
}

/**
 * Pool partagé. Toutes les images sont >= 1200px de large (recommandation Google
 * pour Rich Results Article). Filenames optimisés SEO (mots-clés métier).
 */
export const BLOG_IMAGE_POOL: BlogImage[] = [
  // Photos métier existantes (déjà référencées dans sitemap-images racine)
  {
    path: "/images/coachs-crossfit-chistera-saint-pierre-de-chandieu.jpg",
    alt: "Coachs CrossFit certifiés Level 2 de CrossFit Chistera à Saint-Pierre-de-Chandieu",
    title: "Coachs CrossFit Chistera",
    caption: "Coachs CrossFit certifiés Level 2 de CrossFit Chistera, box affiliée dans l'Est lyonnais.",
    width: 1600,
    height: 1067,
  },
  {
    path: "/images/communaute-box-crossfit-est-lyonnais.jpg",
    alt: "Communauté de la box CrossFit Chistera à Saint-Pierre-de-Chandieu",
    title: "Communauté CrossFit Chistera",
    caption: "Communauté de la box CrossFit Chistera à Saint-Pierre-de-Chandieu.",
    width: 1600,
    height: 1067,
  },
  {
    path: "/images/entrainement-crossfit-hyrox-athx-chistera.jpg",
    alt: "Entraînement fonctionnel CrossFit, Hyrox, ATHX et callisthénie à la box Chistera",
    title: "Entraînement CrossFit Hyrox ATHX",
    caption: "Entraînement fonctionnel : CrossFit, hybrid training, Hyrox, ATHX, callisthénie.",
    width: 1600,
    height: 1067,
  },
  {
    path: "/images/evenement-communautaire-box-crossfit-chistera.jpg",
    alt: "Événement communautaire à la box CrossFit Chistera",
    title: "Événement CrossFit Chistera",
    caption: "Vie communautaire à la box CrossFit Chistera de Saint-Pierre-de-Chandieu.",
    width: 1600,
    height: 1067,
  },
  {
    path: "/images/engagement-sport-sante-crossfit-chistera-ligue-cancer.jpg",
    alt: "Don de 1 000 € à la Ligue contre le cancer du Rhône par CrossFit Chistera",
    title: "Engagement sport santé Chistera",
    caption: "Don de 1 000 € à la Ligue contre le cancer du Rhône par CrossFit Chistera.",
    width: 1600,
    height: 1067,
  },
  // Nouvelles photos (uploads utilisateur, optimisées 1440px)
  {
    path: "/images/blog/01-coachs-athletes-box-chistera.jpg",
    alt: "Coachs et athlètes posant devant le logo de la box CrossFit Chistera",
    title: "Athlètes CrossFit Chistera devant le logo",
    caption: "Coachs et athlètes de la box CrossFit Chistera à Saint-Pierre-de-Chandieu.",
    width: 1440,
    height: 1402,
  },
  {
    path: "/images/blog/02-equipe-competition-black-lions.jpg",
    alt: "Équipes CrossFit Chistera à la compétition Black Lions",
    title: "Compétition Black Lions — équipes Chistera",
    caption: "Équipes CrossFit Chistera (Benbou Programming, Chistewish, La Chist Familly, Au Azaaaar) à la compétition Black Lions.",
    width: 1440,
    height: 1086,
  },
  {
    path: "/images/blog/03-communaute-box-chistera-evenement.jpg",
    alt: "Communauté de la box CrossFit Chistera réunie pour un événement",
    title: "Communauté CrossFit Chistera",
    caption: "Communauté CrossFit Chistera réunie à la box pour un événement intra-club.",
    width: 1440,
    height: 1582,
  },
  {
    path: "/images/blog/04-coach-portrait-chistera.jpg",
    alt: "Portrait d'un coach CrossFit Chistera devant la fresque du logo",
    title: "Portrait coach CrossFit Chistera",
    caption: "Coach CrossFit Chistera, certifié Level 2, à la box de Saint-Pierre-de-Chandieu.",
    width: 1440,
    height: 1911,
  },
  {
    path: "/images/blog/05-athletes-haltere-echobike-crossfit.jpg",
    alt: "Athlètes CrossFit Chistera : haltère, snatch et Echo Bike",
    title: "Athlètes CrossFit Chistera en mouvement",
    caption: "Athlètes CrossFit Chistera : dumbbell snatch, push press et Echo Bike.",
    width: 1440,
    height: 1426,
  },
  {
    path: "/images/blog/06-octobre-rose-cours-collectif-chistera.jpg",
    alt: "Cours collectif Octobre Rose à la box CrossFit Chistera",
    title: "Octobre Rose CrossFit Chistera",
    caption: "Cours collectif Octobre Rose à la box CrossFit Chistera, soutien à la Ligue contre le cancer.",
    width: 1440,
    height: 1892,
  },
  {
    path: "/images/blog/07-athletes-competition-feminines.jpg",
    alt: "Deux athlètes féminines CrossFit Chistera après une compétition",
    title: "Athlètes féminines Chistera",
    caption: "Athlètes CrossFit Chistera après une compétition d'équipe.",
    width: 1358,
    height: 1440,
  },
  {
    path: "/images/blog/08-don-ligue-cancer-esprit-crossfit.jpg",
    alt: "Remise du chèque de 1 000 € à la Ligue contre le cancer par CrossFit Chistera",
    title: "Don Ligue contre le cancer — Chistera",
    caption: "Remise du chèque Esprit CrossFit 2025 à la Ligue contre le cancer du Rhône.",
    width: 1049,
    height: 1390,
  },
  {
    path: "/images/blog/09-calendrier-esprit-crossfit-2025.jpg",
    alt: "Calendrier Esprit CrossFit 2025 au profit de la Ligue contre le cancer",
    title: "Calendrier Esprit CrossFit 2025",
    caption: "Calendrier Esprit CrossFit 2025, projet caritatif de CrossFit Chistera au profit de la Ligue contre le cancer.",
    width: 1439,
    height: 1437,
  },
  {
    path: "/images/blog/10-photo-groupe-adherents-chistera.jpg",
    alt: "Photo de groupe des adhérents CrossFit Chistera",
    title: "Adhérents CrossFit Chistera",
    caption: "Photo de groupe des adhérents CrossFit Chistera à la box de Saint-Pierre-de-Chandieu.",
    width: 1439,
    height: 1033,
  },
  // Octobre Rose 2025 — cours collectif féminin caritatif
  {
    path: "/images/blog/11-octobre-rose-cours-feminin-saint-pierre-de-chandieu.jpg",
    alt: "Cours collectif Octobre Rose 100 % féminin à CrossFit Chistera, box de fitness à Saint-Pierre-de-Chandieu (Est lyonnais)",
    title: "Octobre Rose 2025 — CrossFit Chistera Saint-Pierre-de-Chandieu",
    caption: "Octobre Rose à CrossFit Chistera : cours collectif féminin caritatif à la box de fitness de Saint-Pierre-de-Chandieu, au service des habitantes de Heyrieux, Chaponnay, Mions, Saint-Priest et de l'Est lyonnais, au profit de la Ligue contre le cancer du Rhône.",
    width: 1440,
    height: 1892,
  },
  // Remise du chèque de 1 000 € à la Ligue contre le cancer
  {
    path: "/images/blog/12-remise-cheque-ligue-cancer-rhone-chistera.jpg",
    alt: "Remise d'un chèque de 1 000 € à la Ligue contre le cancer du Rhône par la communauté CrossFit Chistera (Saint-Pierre-de-Chandieu, Est lyonnais)",
    title: "Don 1 000 € Ligue contre le cancer — CrossFit Chistera Est lyonnais",
    caption: "Adhérents et coachs CrossFit Chistera remettent un chèque de 1 000 € à la Ligue contre le cancer du Rhône : engagement caritatif d'une box de CrossFit et fitness à Saint-Pierre-de-Chandieu, rayonnant sur Heyrieux, Chaponnay, Mions, Saint-Priest et l'agglomération de Lyon.",
    width: 1049,
    height: 1390,
  },
  // Calendrier Esprit CrossFit 2025
  {
    path: "/images/blog/13-calendrier-esprit-crossfit-2025-chistera-est-lyonnais.jpg",
    alt: "Calendrier Esprit CrossFit 2025 de CrossFit Chistera (Saint-Pierre-de-Chandieu, Est lyonnais) au profit de la Ligue contre le cancer du Rhône",
    title: "Calendrier Esprit CrossFit 2025 — Chistera & Ligue contre le cancer",
    caption: "Calendrier caritatif « Esprit CrossFit 2025 » réalisé par CrossFit Chistera, box de CrossFit et fitness à Saint-Pierre-de-Chandieu (Est lyonnais, proche Heyrieux, Chaponnay, Mions, Saint-Priest et Lyon), au profit de la Ligue contre le cancer du Rhône.",
    width: 1439,
    height: 1437,
  },
  // --- Photos Ligue contre le cancer du Rhône (article événements Chistera) ---
  {
    path: "/images/serp/calendrier-2025-esprit-crossfit-chistera-ligue-cancer-rhone.jpg",
    alt: "Couverture du calendrier Esprit CrossFit 2025 réalisé par CrossFit Chistera (box CrossFit, cross-training et fitness à Saint-Pierre-de-Chandieu, Est lyonnais) au profit de la Ligue contre le cancer du Rhône",
    title: "Calendrier Esprit CrossFit 2025 — CrossFit Chistera × Ligue contre le cancer du Rhône",
    caption: "Calendrier caritatif « Esprit CrossFit 2025 » de CrossFit Chistera (Saint-Pierre-de-Chandieu) — bénéfices reversés à la Ligue contre le cancer du Rhône, communauté Bonnefamille, Heyrieux, Chaponnay, Mions, Saint-Laurent-de-Mure, Saint-Bonnet-de-Mure, Saint-Priest, Corbas, Meyzieu, Diémoz et Lyon.",
    width: 1200,
    height: 1200,
  },
  {
    path: "/images/serp/cheque-1000-euros-ligue-cancer-rhone-crossfit-chistera.jpg",
    alt: "Remise officielle du chèque de 1 000 € à la Ligue contre le cancer du Rhône par les adhérents et coachs CrossFit Chistera (box CrossFit, cross-training, fitness Saint-Pierre-de-Chandieu, Est lyonnais)",
    title: "Don 1 000 € Ligue contre le cancer du Rhône — CrossFit Chistera",
    caption: "Remise du chèque de 1 000 € à la Ligue contre le cancer du Rhône par la communauté CrossFit Chistera (Saint-Pierre-de-Chandieu, Bonnefamille, Heyrieux, Chaponnay, Mions, Saint-Laurent-de-Mure, Saint-Bonnet-de-Mure, Saint-Priest, Corbas, Meyzieu, Diémoz, Lyon).",
    width: 1080,
    height: 1080,
  },
  {
    path: "/images/serp/affiche-chistera-rose-wod-feminin-octobre-rose-saint-pierre-de-chandieu.jpg",
    alt: "Affiche Chistera Rose — WOD 100 % féminin Octobre Rose à la box CrossFit Chistera de Saint-Pierre-de-Chandieu (cross-training, fitness, Est lyonnais)",
    title: "Chistera Rose — WOD féminin Octobre Rose CrossFit Chistera",
    caption: "Affiche « Chistera Rose » : WOD 100 % féminin Octobre Rose chez CrossFit Chistera, coaché par Marlène, au profit de la Ligue contre le cancer du Rhône (Saint-Pierre-de-Chandieu, Lyon Est).",
    width: 320,
    height: 372,
  },
];

/** URL absolue pour JSON-LD / og:image / sitemap. */
export function absoluteImageUrl(img: BlogImage): string {
  return SITE + img.path;
}

/** Hash 32-bit déterministe (FNV-1a) sur une chaîne. */
function fnv1a(input: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return h >>> 0;
}

/**
 * Sélectionne `count` images du pool de manière déterministe à partir d'un slug.
 * Le slug "crossfit-saint-priest" rendra toujours les 5 mêmes images dans le même ordre.
 * Slugs différents → ordres et sous-ensembles différents (rotation).
 */
export function getBlogImagesForSlug(slug: string, count = 5): BlogImage[] {
  const n = BLOG_IMAGE_POOL.length;
  const safeCount = Math.min(count, n);
  const seed = fnv1a(slug || "default");
  const startOffset = seed % n;
  // Pas premier-ish à n=15 pour bien mélanger sans collision
  const stride = (seed % 7) + 2; // 2..8
  const picked = new Set<number>();
  const out: BlogImage[] = [];
  let i = 0;
  while (out.length < safeCount && i < n * 4) {
    const idx = (startOffset + i * stride) % n;
    if (!picked.has(idx)) {
      picked.add(idx);
      out.push(BLOG_IMAGE_POOL[idx]);
    }
    i++;
  }
  return out;
}

/** Extrait un slug depuis une URL d'article (ex: ".../blog/foo" → "foo"). */
export function slugFromUrl(url: string): string {
  const m = url.match(/\/blog\/([^/?#]+)/);
  return m ? m[1] : url;
}
